import { useEffect, useRef, useState } from 'react';
import { COLORS } from '../../constants/theme';
import { MobileTargetResultCard } from './MobileTargetResultCard';
import { MobileTargetFilterSheet } from './MobileTargetFilterSheet';
import { MobileTargetDetailView } from './MobileTargetDetailView';
import type { TargetFinderShellProps } from '../../pages/TargetFinder';
import type { SortKey } from '../../utils/targetSearchParams';

interface SortOption {
  value: string;
  sortBy: SortKey;
  sortDir: 'asc' | 'desc';
  label: string;
}

// 7 SortKey values x 2 directions = 14 combined key+direction options, covering the same 7
// sortable columns TargetFinderTab.tsx's desktop column headers do.
const SORT_OPTIONS: SortOption[] = [
  { value: 'nw-desc', sortBy: 'nw', sortDir: 'desc', label: 'Networth (High to Low)' },
  { value: 'nw-asc', sortBy: 'nw', sortDir: 'asc', label: 'Networth (Low to High)' },
  { value: 'land-desc', sortBy: 'land', sortDir: 'desc', label: 'Land (High to Low)' },
  { value: 'land-asc', sortBy: 'land', sortDir: 'asc', label: 'Land (Low to High)' },
  { value: 'nwpa-desc', sortBy: 'nwpa', sortDir: 'desc', label: 'NW/Acre (High to Low)' },
  { value: 'nwpa-asc', sortBy: 'nwpa', sortDir: 'asc', label: 'NW/Acre (Low to High)' },
  { value: 'race-desc', sortBy: 'race', sortDir: 'desc', label: 'Race (Z to A)' },
  { value: 'race-asc', sortBy: 'race', sortDir: 'asc', label: 'Race (A to Z)' },
  { value: 'rank-desc', sortBy: 'rank', sortDir: 'desc', label: 'Rank (High to Low)' },
  { value: 'rank-asc', sortBy: 'rank', sortDir: 'asc', label: 'Rank (Low to High)' },
  { value: 'kdNw-desc', sortBy: 'kdNw', sortDir: 'desc', label: 'Kingdom Networth (High to Low)' },
  { value: 'kdNw-asc', sortBy: 'kdNw', sortDir: 'asc', label: 'Kingdom Networth (Low to High)' },
  { value: 'kdLand-desc', sortBy: 'kdLand', sortDir: 'desc', label: 'Kingdom Land (High to Low)' },
  { value: 'kdLand-asc', sortBy: 'kdLand', sortDir: 'asc', label: 'Kingdom Land (Low to High)' },
];

/**
 * SF-171 Component 5: mobile list view — header, results-count line, "Filters" trigger with an
 * active-count badge, a sort `<select>` (replacing desktop's sortable-column-header table), the
 * card list, and a Previous/Next pager. Orchestrates which of list / filter sheet / target-detail
 * is showing based on `selectedTarget` (derived, page-owned) and a local `filtersOpen` boolean —
 * the only remaining shell-local (non-page) piece of UI state besides the sheet's own draft.
 */
export function MobileTargetFinderShell(props: TargetFinderShellProps) {
  const {
    worldId, provinceId, results, loading, error,
    mobileFilters, onMobileFiltersChange, sortBy, sortDir, onSort,
    page, onPageChange, selectedTarget, onSelectTarget, currentTick,
  } = props;
  const [filtersOpen, setFiltersOpen] = useState(false);
  // Tracks the last-viewed target's id so, on returning to the list, focus can be restored to
  // that card's button rather than left at document.body.
  const lastSelectedIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (selectedTarget) {
      lastSelectedIdRef.current = selectedTarget.provinceId;
      return;
    }
    const lastId = lastSelectedIdRef.current;
    if (lastId) {
      lastSelectedIdRef.current = null;
      const card = document.querySelector<HTMLElement>(`[data-target-id="${lastId}"]`);
      card?.focus();
    }
  }, [selectedTarget]);

  if (selectedTarget) {
    return (
      <MobileTargetDetailView
        worldId={worldId}
        provinceId={provinceId}
        target={selectedTarget}
        currentTick={currentTick}
        onBack={() => onSelectTarget(null)}
      />
    );
  }

  const provinceFilterCount = [
    mobileFilters.raceEnabled, mobileFilters.rankEnabled, mobileFilters.nwEnabled,
    mobileFilters.nwpaEnabled, mobileFilters.landEnabled,
  ].filter(Boolean).length;
  const kingdomFilterCount = [mobileFilters.kdNwEnabled, mobileFilters.kdLandEnabled].filter(Boolean).length;
  const totalFilterCount = provinceFilterCount + kingdomFilterCount;

  const totalCount = results?.totalCount ?? 0;
  const size = results?.size ?? 20;
  const rangeStart = totalCount === 0 ? 0 : page * size + 1;
  const rangeEnd = Math.min(totalCount, (page + 1) * size);
  const totalPages = Math.max(1, Math.ceil(totalCount / size));
  const currentSortValue = `${sortBy}-${sortDir}`;

  function handleSortChange(value: string) {
    const opt = SORT_OPTIONS.find((o) => o.value === value);
    if (opt) onSort(opt.sortBy, opt.sortDir);
  }

  return (
    <div style={{ padding: '12px 12px 24px' }}>
      <h1 style={{ fontSize: 18, fontWeight: 700, color: COLORS.text.primary, margin: '4px 0 10px' }}>
        Target Finder
      </h1>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 10 }}>
        <button
          type="button"
          onClick={() => setFiltersOpen(true)}
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            minHeight: 44, padding: '0 14px',
            background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: 6,
            color: COLORS.text.primary, fontSize: 13, fontWeight: 600, cursor: 'pointer',
          }}
        >
          Filters
          {totalFilterCount > 0 && (
            <span
              style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                minWidth: 18, height: 18, padding: '0 4px', borderRadius: 9,
                background: COLORS.link, color: '#fff', fontSize: 10, fontWeight: 700,
              }}
            >
              {totalFilterCount}
            </span>
          )}
        </button>

        <select
          aria-label="Sort by"
          value={currentSortValue}
          onChange={(e) => handleSortChange(e.target.value)}
          style={{
            flex: 1,
            minHeight: 44,
            padding: '0 10px',
            background: COLORS.inputBg,
            border: `1px solid ${COLORS.inputBorder}`,
            borderRadius: 6,
            color: COLORS.text.primary,
            fontSize: 13,
          }}
        >
          {SORT_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>

      {error && (
        <div style={{ color: COLORS.negative, fontSize: 13, padding: '8px 0' }}>{error}</div>
      )}

      {loading && !results && (
        <div style={{ color: COLORS.text.muted, fontSize: 13, textAlign: 'center', padding: 24 }}>
          Searching...
        </div>
      )}

      {results && (
        <>
          <div style={{ color: COLORS.text.muted, fontSize: 11, marginBottom: 8 }}>
            {totalCount === 0 ? 'No results' : `Showing ${rangeStart}–${rangeEnd} of ${totalCount}`}
          </div>

          {totalCount === 0 ? (
            <div style={{ color: COLORS.text.muted, fontSize: 13, textAlign: 'center', padding: 24 }}>
              No provinces match your filters.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {results.results.map((row) => (
                <MobileTargetResultCard
                  key={row.provinceId}
                  row={row}
                  playerNetworth={results.player?.networth ?? null}
                  currentTick={currentTick}
                  onSelect={onSelectTarget}
                />
              ))}
            </div>
          )}

          {totalCount > size && (
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              <button
                type="button"
                onClick={() => onPageChange(page - 1)}
                disabled={page === 0 || loading}
                style={{
                  flex: 1, minHeight: 44, borderRadius: 6,
                  background: COLORS.card, border: `1px solid ${COLORS.border}`,
                  color: COLORS.text.secondary, fontSize: 13,
                  opacity: page === 0 ? 0.4 : 1,
                  cursor: page === 0 ? 'not-allowed' : 'pointer',
                }}
              >
                Previous
              </button>
              <span style={{ alignSelf: 'center', color: COLORS.text.secondary, fontSize: 12 }}>
                Page {page + 1} of {totalPages}
              </span>
              <button
                type="button"
                onClick={() => onPageChange(page + 1)}
                disabled={(page + 1) * size >= totalCount || loading}
                style={{
                  flex: 1, minHeight: 44, borderRadius: 6,
                  background: COLORS.card, border: `1px solid ${COLORS.border}`,
                  color: COLORS.text.secondary, fontSize: 13,
                  opacity: (page + 1) * size >= totalCount ? 0.4 : 1,
                  cursor: (page + 1) * size >= totalCount ? 'not-allowed' : 'pointer',
                }}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {filtersOpen && (
        <MobileTargetFilterSheet
          isOpen={filtersOpen}
          onClose={() => setFiltersOpen(false)}
          filters={mobileFilters}
          playerStats={results?.player ?? null}
          onApply={(filters) => {
            onMobileFiltersChange(filters);
            setFiltersOpen(false);
          }}
        />
      )}
    </div>
  );
}
